/**
 * Asigna image.render SOLO a imágenes del web-image-loader (typeof getImage === 'function').
 *
 * WW/WC en preview: por defecto paint neutro (drawImage, sin context.filter ni color-LUT).
 * El filtro CSS/brightness se aplica en el host DOM solo cuando el usuario mueve WWWC
 * (context.filter + WebP en GPU teñía de rojo en varios drivers).
 *
 * getPixelData del web-loader hace getImageData full-res en cada llamada → freeze;
 * lo sustituimos por cache de un solo readback (willReadFrequently) y marcamos rgba:true.
 */
import cornerstone from 'cornerstone-core'

function getNativeWebImageRenderFn() {
  if (typeof cornerstone.renderWebImage === 'function') {
    return cornerstone.renderWebImage
  }
  if (
    cornerstone &&
    cornerstone.rendering &&
    typeof cornerstone.rendering.renderWebImage === 'function'
  ) {
    return cornerstone.rendering.renderWebImage
  }
  return null
}

function isIdentityPreviewFilter(filter) {
  if (!filter || filter === 'none') return true
  const s = String(filter)
  // brightness(1) contrast(1) sin invert
  if (/invert\s*\(/i.test(s)) return false
  const b = /brightness\(\s*([0-9.]+)\s*\)/i.exec(s)
  const c = /contrast\(\s*([0-9.]+)\s*\)/i.exec(s)
  const bv = b ? Number(b[1]) : 1
  const cv = c ? Number(c[1]) : 1
  return Math.abs(bv - 1) < 0.02 && Math.abs(cv - 1) < 0.02
}

/**
 * Paint WebP/JPG: drawImage directo. Nunca color-LUT de Cornerstone.
 * Nunca context.filter (artefacto rojo con WebP en algunos GPUs).
 *
 * @param {object} enabledElement
 * @param {boolean} [_invalidated]
 */
export function renderWebPreviewImage(enabledElement, _invalidated) {
  if (!enabledElement) return
  const image = enabledElement.image
  if (!image || typeof image.getImage !== 'function') return

  const canvas = enabledElement.canvas
  if (!canvas) return

  const w = canvas.width | 0
  const h = canvas.height | 0
  if (w <= 0 || h <= 0) return

  let htmlImg = null
  try {
    htmlImg = image.getImage()
  } catch (e) {
    htmlImg = null
  }
  if (!htmlImg) return

  const viewport = enabledElement.viewport || {}
  // Forzar VOI identidad en el objeto (por si otro path lee image.window*)
  image.windowWidth = WEB_PREVIEW_DISPLAY_VOI.windowWidth
  image.windowCenter = WEB_PREVIEW_DISPLAY_VOI.windowCenter

  const host = enabledElement.element
  const baked =
    (typeof canvas._udvPreviewFilter === 'string' && canvas._udvPreviewFilter) ||
    (host && typeof host._udvPreviewFilter === 'string' && host._udvPreviewFilter) ||
    'none'
  const identity = isIdentityPreviewFilter(baked)

  // Filtro WWWC en el HOST (no context.filter / no canvas.style): evita tinte rojo GPU.
  try {
    if (host && host.style) {
      if (identity) {
        if (host.style.filter) host.style.filter = ''
      } else {
        host.style.filter = baked
      }
    }
    if (canvas.style && canvas.style.filter) canvas.style.filter = ''
  } catch (e) {
    /* ignore */
  }

  const vis = canvas.getContext('2d', { alpha: false })
  if (!vis) return

  try {
    vis.setTransform(1, 0, 0, 1, 0, 0)
    vis.filter = 'none'
    vis.globalCompositeOperation = 'source-over'
    vis.fillStyle = '#000000'
    vis.fillRect(0, 0, w, h)
  } catch (e) {
    /* ignore */
  }

  vis.imageSmoothingEnabled = !viewport.pixelReplication
  if ('mozImageSmoothingEnabled' in vis) {
    vis.mozImageSmoothingEnabled = vis.imageSmoothingEnabled
  }

  try {
    if (typeof cornerstone.setToPixelCoordinateSystem === 'function') {
      cornerstone.setToPixelCoordinateSystem(enabledElement, vis)
    }
  } catch (e) {
    /* ignore */
  }

  let sx = 0
  let sy = 0
  let width = image.width || image.columns || htmlImg.naturalWidth || 1
  let height = image.height || image.rows || htmlImg.naturalHeight || 1
  try {
    if (typeof cornerstone.getDisplayedArea === 'function') {
      const area = cornerstone.getDisplayedArea(image, viewport)
      if (area && area.tlhc && area.brhc) {
        sx = area.tlhc.x - 1
        sy = area.tlhc.y - 1
        width = area.brhc.x - sx
        height = area.brhc.y - sy
      }
    }
  } catch (e) {
    /* full */
  }

  try {
    vis.filter = 'none'
    vis.drawImage(htmlImg, sx, sy, width, height, sx, sy, width, height)
  } catch (e) {
    try {
      vis.drawImage(htmlImg, 0, 0)
    } catch (e2) {
      /* ignore */
    }
  }
}

/**
 * Un solo readback de pixeles (con willReadFrequently). Evita tormenta de
 * getImageData del createImage del web-image-loader.
 * @param {object} image
 */
export function hardenWebPreviewPixelAccess(image) {
  if (!image || typeof image.getImage !== 'function') return image
  if (image._udvPixelHardened) return image

  const getHtml = () => {
    try {
      return image.getImage()
    } catch (e) {
      return null
    }
  }

  let cachedData = null
  const origGetPixel =
    typeof image.getPixelData === 'function' ? image.getPixelData.bind(image) : null

  image.getPixelData = function udvCachedGetPixelData() {
    if (cachedData) return cachedData
    try {
      const html = getHtml()
      const w = (html && (html.naturalWidth || html.width)) || image.width || image.columns || 0
      const h =
        (html && (html.naturalHeight || html.height)) || image.height || image.rows || 0
      if (html && w > 0 && h > 0) {
        const c = document.createElement('canvas')
        c.width = w
        c.height = h
        // alpha:true — ImageData siempre es RGBA; cornerstone color path salta alpha.
        const ctx = c.getContext('2d', { willReadFrequently: true, alpha: true })
        if (ctx) {
          ctx.drawImage(html, 0, 0)
          cachedData = ctx.getImageData(0, 0, w, h).data
          image._udvPixelCache = cachedData
          return cachedData
        }
      }
    } catch (e) {
      /* fallthrough */
    }
    if (origGetPixel) {
      try {
        cachedData = origGetPixel()
        return cachedData
      } catch (e2) {
        /* ignore */
      }
    }
    const px = (image.width || 1) * (image.height || 1) * 4
    cachedData = new Uint8ClampedArray(px)
    return cachedData
  }

  image._udvPixelHardened = true
  return image
}

/**
 * @param {object|null|undefined} image
 * @returns {boolean}
 */
export function canUseWebImageRender(image) {
  return !!(image && typeof image.getImage === 'function')
}

/**
 * True si es preview web (loader jpg/webp), no DICOM color.
 */
export function isWebPreviewImage(image, fileData) {
  if (image && typeof image.getImage === 'function') return true
  if (fileData && fileData.renderKind === 'preview') {
    if (!image || !image.data) return true
    return false
  }
  if (image && !image.data && typeof image.getImage !== 'function') {
    return false
  }
  return false
}

/**
 * Defaults clínicos cuando no hay tags de ventana (CT-like).
 * Usados como ancla del WebP (brightness=1, contrast=1 ≈ esta ventana).
 */
export const DEFAULT_CLINICAL_WW = 400
export const DEFAULT_CLINICAL_WC = 50

/** VOI neutro para WebP/JPG ya ventaneados (no aplicar WC clínico del manifest). */
export const WEB_PREVIEW_DISPLAY_VOI = Object.freeze({
  windowWidth: 255,
  windowCenter: 128,
})

/**
 * Fija VOI 255/128 en viewport de preview web (evita tinte rojo con WC=0 del manifest).
 * @param {object|null|undefined} viewport
 * @returns {object|null|undefined}
 */
export function applyNeutralWebPreviewVoi(viewport) {
  if (!viewport) return viewport
  if (!viewport.voi) viewport.voi = {}
  viewport.voi.windowWidth = WEB_PREVIEW_DISPLAY_VOI.windowWidth
  viewport.voi.windowCenter = WEB_PREVIEW_DISPLAY_VOI.windowCenter
  viewport.invert = false
  return viewport
}

/**
 * Asegura metadatos VOI 8-bit en la imagen web (createImage / cache).
 * Siempre pisa valores clínicos colados desde el manifest (WC=0 → LUT rojo).
 * @param {object} image
 */
export function hardenWebPreviewImageVoi(image) {
  if (!image || typeof image.getImage !== 'function') return image
  image.windowWidth = WEB_PREVIEW_DISPLAY_VOI.windowWidth
  image.windowCenter = WEB_PREVIEW_DISPLAY_VOI.windowCenter
  if (image.slope == null) image.slope = 1
  if (image.intercept == null) image.intercept = 0
  image.color = true
  // getPixelData es RGBA (ImageData); el flag del web-loader venía en false.
  image.rgba = true
  try {
    if (image.cachedLut) delete image.cachedLut
  } catch (e) {
    image.cachedLut = undefined
  }
  return image
}

/**
 * CSS filter string desde brightness/contrast/invert.
 * @param {{ brightness?: number, contrast?: number, invert?: boolean }} state
 * @returns {string}
 */
export function previewWwwcCssFilter(state) {
  const b = Math.max(0.15, Math.min(4, Number(state && state.brightness) || 1))
  const c = Math.max(0.15, Math.min(6, Number(state && state.contrast) || 1))
  let f = `brightness(${b}) contrast(${c})`
  if (state && state.invert) f += ' invert(1)'
  return f
}

/**
 * Mapea WW/WC clínicos a brightness/contrast CSS (preview 8-bit).
 * Ancla: wwRef/wcRef = ventana con la que se generó el WebP (look neutro).
 * @param {number} ww
 * @param {number} wc
 * @param {number} [wwRef]
 * @param {number} [wcRef]
 * @returns {{ brightness: number, contrast: number }}
 */
export function logicalWwwcToCss(
  ww,
  wc,
  wwRef = DEFAULT_CLINICAL_WW,
  wcRef = DEFAULT_CLINICAL_WC
) {
  const wr = Math.max(1, Number(wwRef) || DEFAULT_CLINICAL_WW)
  const cr = Number.isFinite(Number(wcRef)) ? Number(wcRef) : DEFAULT_CLINICAL_WC
  const w = Math.max(1, Number(ww) || wr)
  const c = Number.isFinite(Number(wc)) ? Number(wc) : cr
  return {
    contrast: Math.max(0.15, Math.min(6, wr / w)),
    brightness: Math.max(0.15, Math.min(4, 1 + (cr - c) / wr)),
  }
}

/**
 * Inverso aproximado CSS → WW/WC clínicos (para migraciones).
 * @param {number} brightness
 * @param {number} contrast
 * @param {number} [wwRef]
 * @param {number} [wcRef]
 * @returns {{ ww: number, wc: number }}
 */
export function cssToLogicalWwwc(
  brightness,
  contrast,
  wwRef = DEFAULT_CLINICAL_WW,
  wcRef = DEFAULT_CLINICAL_WC
) {
  const wr = Math.max(1, Number(wwRef) || DEFAULT_CLINICAL_WW)
  const cr = Number.isFinite(Number(wcRef)) ? Number(wcRef) : DEFAULT_CLINICAL_WC
  const c = Math.max(0.15, Number(contrast) || 1)
  const b = Math.max(0.15, Number(brightness) || 1)
  return {
    ww: Math.max(1, Math.round(wr / c)),
    wc: Math.round(cr - (b - 1) * wr),
  }
}

/**
 * @param {object|null|undefined} image
 * @param {object|null|undefined} [fileData]
 * @returns {object|null|undefined}
 */
export function ensureImageRender(image, fileData) {
  if (!image) return image

  const nativeWeb = getNativeWebImageRenderFn()

  if (canUseWebImageRender(image)) {
    hardenWebPreviewPixelAccess(image)
    hardenWebPreviewImageVoi(image)
    image.render = renderWebPreviewImage
    return image
  }

  if (
    image.render === renderWebPreviewImage ||
    (nativeWeb && image.render === nativeWeb)
  ) {
    try {
      delete image.render
    } catch (e) {
      image.render = undefined
    }
  }

  return image
}
